import React, { useEffect, useState } from "react";
import CreatePage from "../../../../components/CreatePage";
import UserRole from "../../../../Models/user/UserRole";
import { FieldValidator } from "../../../../components/FieldValidator";
import {
  CancelButton,
  SubmitButton,
} from "../../../../components/Button/button";
import { useHistory } from "react-router-dom";
import { Box } from "@material-ui/core";
import BackendServices from "../../../../components/Api/BackendService";
import {
  companyCreateOutside,
  createUser,
} from "../../../../components/Paths/paths";
import Snackbar from "../../../../components/Snackbar";
import NamedEntity from "../../../../Models/APIGetData/NamedEntity";
import { PostUserBody } from "../../../UserManagement/UserNew/http/postUser";
import axios from "axios";

interface CompanyDetailsProps {
  company: {
    name: string;
    location: {
      addressLine1: string | undefined;
      addressLine2: string | undefined;
      addressLine3: string | undefined;
      city: string | undefined;
      county: string | undefined;
      zipCode: string | undefined;
      country: NamedEntity | null;
    };
    website: string;
    blockSharing: boolean;
  };
  contact: {
    contactDetails: {
      phoneNumber: string | undefined;
      faxNumber: string | undefined;
      email: string | undefined;
      name: string | undefined;
    };
  };
  natureOfBusinessId: string | number | undefined;
}

interface UserSignUpFormProps {
  companyName: string;
  companyDetails: CompanyDetailsProps;
}

export interface CheckUserInfo {
  isPresent: boolean;
  role: boolean;
  primaryOrganisation: boolean
}

const UserSignUpForm = ({
  companyName,
  companyDetails,
}: UserSignUpFormProps) => {
  const [roles, setRoles] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [submitting, isSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | React.ReactNode>("");
  const [messageType, setMessageType] = useState<"success" | "error">();
  const [companyId, setCompanyId] = useState<number>(0);
  const [jobTitle, setJobTitle] = useState<string>('');


  const url=`${process.env.REACT_APP_AUTH_REDIRECT}`;
  const redirecturi=`${encodeURIComponent(url)}`;
  const history = useHistory();

  useEffect(() => {
    roles.push(UserRole.COMPANY_USER);
  }, []);

  const submitUser = async (returnedCompanyId: number) => {
    const user: PostUserBody = {
        email: email,
        firstName: firstName,
        jobTitle: jobTitle,
        lastName: lastName,
        overrideWarning: false,
        phoneNumber: phoneNumber,
        primaryOrganisation: {
            externalId: returnedCompanyId,
            name: companyName,
            type: 1,
        },
        role: "Company User",
        secondaryOrganisations: [],
        enablemailLitetoPro: false
    };
    try {
      const userRes = await axios.post(
        `${process.env.REACT_APP_AXIOS_API}${BackendServices.USER_SERVICE.USER}${createUser}`,
        user
      );
      if (userRes.status === 200) {
        window.location.href = `${process.env.REACT_APP_PASSWORD_RESET_URL}?p=${process.env.REACT_APP_PASSWORD_RESET_POLICY}&client_id=${process.env.REACT_APP_AUTH_APPLICATION}&nonce=defaultNonce&redirect_uri=${redirecturi}&scope=openid&response_type=id_token&prompt=login&login_hint=${email}`;
        return Promise.resolve();
      }
    } catch (error) {
      setMessage(
        "There was an error while creating your company. Please try again after sometime."
      );
      setMessageType("error");
      setOpen(true);
      return Promise.reject(error);
    } finally {
      isSubmitting(false);
    }
  };

  const submitCompany = async () => {
    let returnedCompanyId = 0;

    try {
      // Creating the company
      const returnedCompanyResponse = await axios.post<number>(
        `${process.env.REACT_APP_AXIOS_API}${BackendServices.COMPANY_SERVICE.COMPANY}${companyCreateOutside}`,
        companyDetails
      );
      setCompanyId(returnedCompanyResponse.data);
      returnedCompanyId = returnedCompanyResponse.data;
      submitUser(returnedCompanyId);
    } catch (error) {
      if (!returnedCompanyId) {
        setMessage(`Company Creation Failed. Please try again.`);
        setMessageType("error");
        setOpen(true);
        return Promise.reject();
      }
      isSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      isSubmitting(true);
      const checkUser = await axios
        .get<CheckUserInfo>(
          `${process.env.REACT_APP_AXIOS_API}${BackendServices.USER_SERVICE.USER}/checkUserInfo?email=${email}`
        )
        .then(({ data }) => {
          return data;
        }); 
      if (checkUser.isPresent && checkUser.role && checkUser.primaryOrganisation){
        isSubmitting(false);
        setMessage(
        <span>
          A Directory Lite account could not be created because the email provided is already linked to an account in Directory. Please use the {' '}
          <a href={`${process.env.REACT_APP_PASSWORD_RESET_URL}?p=${process.env.REACT_APP_PASSWORD_RESET_POLICY}&client_id=${process.env.REACT_APP_AUTH_APPLICATION}&nonce=defaultNonce&redirect_uri=${redirecturi}&scope=openid&response_type=id_token&prompt=login`}>Forgot Password</a>
          {' '}service to re-activate your profile.
        </span>);
        setMessageType("error");
        setOpen(true);
        return Promise.reject();
      }
      else if(checkUser.isPresent || (checkUser.role || checkUser.primaryOrganisation))
      // This condition matches scenarios where isPresent is true, and both role & primaryOrg is false, AND
      // where isPresent is true, and one of role & primaryOrg is true, but NOT match when all of them are false.
      { 
        isSubmitting(false);
        setMessage(
        <span>
          A Directory Lite account could not be created because the email provided has already been used in our systems. Please contact {' '} 
          <a href={`https://form.jotform.com/210703256820043`} target="_blank">BRCGS Support</a>
          {' '} to proceed.
        </span>);
        setMessageType("error");
        setOpen(true);
        return Promise.reject();
      }
      else return submitCompany();
    } catch (error) {
      setMessage(`There was an unexpected error please check console.`);
      setMessageType("error");
      setOpen(true);
      return Promise.reject();
    }
  };

  return (
    <>
      <CreatePage
        title="User Information"
        onSubmit={handleSubmit}
        fields={[
          {
            name: "Email",
            label: "Email",
            value: email,
            setTextValue: setEmail,
            validators: [FieldValidator.EMAIL],
            required: true,
          },
          {
            name: "FirstName",
            label: "First Name",
            value: firstName,
            setTextValue: setFirstName,
            required: true,
          },
          {
            name: "LastName",
            label: "Last Name",
            value: lastName,
            setTextValue: setLastName,
            required: true,
          },
          {
            name: "JobTitle",
            label: "Job Title",
            value: jobTitle,
            setTextValue: setJobTitle,
            required: true
          },
          {
            name: "PhoneNumber",
            label: "Phone number",
            value: phoneNumber,
            setTextValue: setPhoneNumber,
            validators: [FieldValidator.PHONE_NUMBER],
          },
        ]}
        actionButtonArea={
          <Box style={{ display: "flex", gap: "2", padding: "12px" }}>
            <SubmitButton disabled={submitting} text="Add User" />
            <CancelButton
              onClick={() => history.push("/")}
              disabled={submitting}
            />
          </Box>
        }
      />
      <Snackbar
        open={open}
        hideAfter={null}
        close={() => setOpen(false)}
        message={message}
        type={messageType}
      />
    </>
  );
};

export default UserSignUpForm;
